import { z } from 'zod'

import BookType from 'constants/BookType'

import {
  asc35060ReportSchema,
  assetReportSchema,
  balanceRowSchema,
  balanceSchema,
  fairMarketValueLotDispositionsRowSchema,
  fairMarketValueLotDispositionsSchema,
  fairMarketValueLotEndingInventoryRowSchema,
  fairMarketValueLotEndingInventorySchema,
  fairMarketValueLotLevelRowSchema,
  fairMarketValueLotLevelSchema,
  fairMarketValueSummaryRowSchema,
  fairMarketValueSummarySchema,
  gainLossReportRowSchema,
  gainLossReportSchema,
  gainlossSummaryRowSchema,
  impairmentDispositionRowSchema,
  impairmentDispositionSchema,
  qaImpairmentSummaryRowSchema,
  qaImpairmentSummarySchema,
  impairmentLotEndingInventoryRowSchema,
  impairmentLotEndingInventorySchema,
  impairmentLotLevelReportSchema,
  impairmentSummaryReportSchema,
  journalEntryDetailedRowSchema,
  ledgerReportSchema,
  legacyRollForwardSchema,
  nftRowSchema,
  pricesDailySummarySchema,
  qaAssetDataSchema,
  qaBalanceRowSchema,
  qaBalanceSchema,
  qaFairMarketValueSummaryRowSchema,
  qaFairMarketValueSummarySchema,
  qaRollForwardRowSchema,
  qaRollForwardSchema,
  qaSignificantHoldingsRowSchema,
  qaSignificantHoldingsSchema,
  qaGlobalEntityHoldingsRowSchema,
  qaGlobalEntityHoldingsSchema,
  qaGlobalAssetHoldingsRowSchema,
  qaGlobalAssetHoldingsSchema,
  reconciliationRollForwardReportSchema,
  reportPaginationSchema,
  rollingGainLossReportSchema,
  significantHoldingsDetailedRowSchema,
  significantHoldingsDetailedSchema,
  significantHoldingsReportSchema,
  qaJournalEntryDetailedRowSchema,
  journalEntrySummaryRowSchema,
  transactionEditHistoryRowSchema,
  transactionEditHistorySchema,
} from './reportApiTypeSchemas'
import { Transaction } from '../transaction/transactionApiTypes'

export enum ReportType {
  Asset = 'asset',
  Balance = 'balance',
  GainLoss = 'gainloss',
  Nft = 'nft',
  RollForward = 'roll_forward',
  JournalEntryDetailed = 'journal_entry_detailed',
  SignificantHoldings = 'significant_holdings',
  ReconciliationRollForward = 'reconciliation_roll_forward',
  RollingGainLoss = 'rolling_gainloss',
  ImpairmentSummary = 'impairment_summary',
  ImpairmentLotLevel = 'impairment_lot_level',
  FairMarketValueSummary = 'fair_market_value_summary',
  FairMarketValueLotLevel = 'fair_market_value_lot_level',
  PricesDailySummary = 'prices_daily_summary',
  QaRollForward = 'qa_roll_forward',
  QaAsset = 'qa_asset',
  QaBalance = 'qa_balance',
  QaFairMarketValueSummary = 'qa_fmv_summary',
  QaSignificantHoldings = 'qa_significant_holdings',
  SignificantHoldingsDetailed = 'significant_holdings_detailed',
  FairMarketValueLotDispositions = 'fmv_lot_dispositions',
  FairMarketValueLotEndingInventory = 'fmv_lot_ending_inventory',
  SummaryStatement = 'summary_statement',
  Asc35060 = 'asc_350_60',
  QaGlobalEntityHoldings = 'global_entity_holdings',
  QaGlobalAssetHoldings = 'global_asset_holdings',
  QaJournalEntryDetailed = 'qa_journal_entry_detailed',
  JournalEntrySummary = 'journal_entry_summary',
  GainlossSummary = 'gainloss_summary',
  TransactionEditHistory = 'transaction_edit_history',
  ImpairmentLotEndingInventory = 'impairment_lot_ending_inventory',
  ImpairmentDisposition = 'impairment_lot_dispositions',
  QaImpairmentSummary = 'qa_impairment_summary',
}

export type FetchPaginatedReportRequestParams = {
  query_execution_id: string
  next_token: string
  page_size: number
  page_num: number
  start: string
  end: string
  source: string[]
  asset: string[]
  version?: string
  transaction_id?: string
}

export type LedgerReportParams = {
  txns: Transaction[]
  startDate: string
  endDate: string
}

export type SpecificReportType =
  | ReportType.Asset
  | ReportType.GainLoss
  | ReportType.RollingGainLoss
  | ReportType.GainlossSummary
  | ReportType.ImpairmentLotLevel
  | ReportType.ImpairmentSummary
  | ReportType.ReconciliationRollForward
  | ReportType.SignificantHoldings
  | ReportType.SignificantHoldingsDetailed
  | ReportType.PricesDailySummary
  | ReportType.QaRollForward
  | ReportType.QaAsset
  | ReportType.QaBalance
  | ReportType.QaSignificantHoldings
  | ReportType.QaFairMarketValueSummary
  | ReportType.FairMarketValueLotDispositions
  | ReportType.FairMarketValueLotEndingInventory
  | ReportType.QaGlobalEntityHoldings
  | ReportType.QaGlobalAssetHoldings
  | ReportType.QaJournalEntryDetailed
  | ReportType.JournalEntrySummary
  | ReportType.TransactionEditHistory
  | ReportType.ImpairmentLotEndingInventory
  | ReportType.ImpairmentDisposition
  | ReportType.QaImpairmentSummary

export type NonSpecificReportType = Exclude<ReportType, SpecificReportType>

export type AllReportTypes = ReportType

export type AssetReportData = z.infer<typeof assetReportSchema>

export type GainLossReportRow = z.infer<typeof gainLossReportRowSchema>
export type GainLossReportData = z.infer<typeof gainLossReportSchema>

export type RollingGainLossReportData = z.infer<typeof rollingGainLossReportSchema>
export type ImpairmentLotLevelReportData = z.infer<typeof impairmentLotLevelReportSchema>
export type ImpairmentSummaryReportData = z.infer<typeof impairmentSummaryReportSchema>
export type LedgerReportData = z.infer<typeof ledgerReportSchema>
export type ReconciliationRollForwardReportData = z.infer<
  typeof reconciliationRollForwardReportSchema
>
export type SignificantHoldingsReportData = z.infer<typeof significantHoldingsReportSchema>
export type PricesDailySummaryData = z.infer<typeof pricesDailySummarySchema>

export type QaRollForwardRow = z.infer<typeof qaRollForwardRowSchema>
export type QaRollForwardData = z.infer<typeof qaRollForwardSchema>

export type QaAssetData = z.infer<typeof qaAssetDataSchema>

export type QaBalanceReportRow = z.infer<typeof qaBalanceRowSchema>
export type QaBalanceData = z.infer<typeof qaBalanceSchema>

export type QaSignificantHoldingsReportRow = z.infer<typeof qaSignificantHoldingsRowSchema>
export type QaSignificantHoldingsData = z.infer<typeof qaSignificantHoldingsSchema>

export type QaGlobalEntityHoldingsReportRow = z.infer<typeof qaGlobalEntityHoldingsRowSchema>
export type QaGlobalEntityHoldingsData = z.infer<typeof qaGlobalEntityHoldingsSchema>

export type QaGlobalAssetHoldingsReportRow = z.infer<typeof qaGlobalAssetHoldingsRowSchema>
export type QaGlobalAssetHoldingsData = z.infer<typeof qaGlobalAssetHoldingsSchema>

export type SignificantHoldingsDetailedRow = z.infer<typeof significantHoldingsDetailedRowSchema>
export type SignificantHoldingsDetailedData = z.infer<typeof significantHoldingsDetailedSchema>

export type BalanceReportRow = z.infer<typeof balanceRowSchema>
export type BalanceData = z.infer<typeof balanceSchema>

export type NftReportRow = z.infer<typeof nftRowSchema>

export type QaFairMarketValueSummaryRow = z.infer<typeof qaFairMarketValueSummaryRowSchema>
export type QaFairMarketValueSummaryData = z.infer<typeof qaFairMarketValueSummarySchema>

export type FairMarketValueLotDispositionsReportRow = z.infer<
  typeof fairMarketValueLotDispositionsRowSchema
>
export type FairMarketValueLotDispositionsData = z.infer<
  typeof fairMarketValueLotDispositionsSchema
>

export type FairMarketValueLotEndingInventoryReportRow = z.infer<
  typeof fairMarketValueLotEndingInventoryRowSchema
>
export type FairMarketValueLotEndingInventoryData = z.infer<
  typeof fairMarketValueLotEndingInventorySchema
>

export type LegacyRollForwardData = z.infer<typeof legacyRollForwardSchema>

export type FairMarketValueLotLevelReportRow = z.infer<typeof fairMarketValueLotLevelRowSchema>
export type FairMarketValueLotLevelReportData = z.infer<typeof fairMarketValueLotLevelSchema>

export type FairMarketValueSummaryReportRow = z.infer<typeof fairMarketValueSummaryRowSchema>
export type FairMarketValueSummaryReportData = z.infer<typeof fairMarketValueSummarySchema>

export type JournalEntryDetailedRow = z.infer<typeof journalEntryDetailedRowSchema>
export type QaJournalEntryDetailedRow = z.infer<typeof qaJournalEntryDetailedRowSchema>

export type JournalEntrySummaryRow = z.infer<typeof journalEntrySummaryRowSchema>

export type GainlossSummaryRow = z.infer<typeof gainlossSummaryRowSchema>

export type ImpairmentLotEndingInventoryReportRow = z.infer<
  typeof impairmentLotEndingInventoryRowSchema
>
export type ImpairmentLotEndingInventoryData = z.infer<typeof impairmentLotEndingInventorySchema>

export type ImpairmentDispositionReportRow = z.infer<typeof impairmentDispositionRowSchema>
export type ImpairmentDispositionData = z.infer<typeof impairmentDispositionSchema>

export type QaImpairmentSummaryReportRow = z.infer<typeof qaImpairmentSummaryRowSchema>
export type QaImpairmentSummaryData = z.infer<typeof qaImpairmentSummarySchema>

export type TransactionEditHistoryRow = z.infer<typeof transactionEditHistoryRowSchema>
export type TransactionEditHistoryData = z.infer<typeof transactionEditHistorySchema>

export type Asc35060ReportData = z.infer<typeof asc35060ReportSchema>

export type ReportRow = Record<string, number>

export const isReportRowArray = (rows: ReportRow | ReportRow[]): rows is ReportRow[] =>
  Array.isArray(rows)
export const isReportRowObject = (rows: ReportRow | ReportRow[]): rows is ReportRow =>
  !Array.isArray(rows)

export type ReconciliationRollForwardResponse = {
  additions: Record<string, number>
  dispositions: Record<string, number>
  fair_value_as_of_from_date: number
  fair_value_as_of_to_date: number
  gainloss: Record<string, number>
}

export type FetchPaginatedReportPaginationParams = z.infer<typeof reportPaginationSchema>

export type FetchPaginatedReportResponse = {
  rows: ReportRow | ReportRow[] // for most reports that are displayed in tables
  headers: string[]
  by_asset?: PaginatedReportByAsset // for significant holdings report
  data?: ReconciliationRollForwardResponse // for reconciliation roll forward report
} & FetchPaginatedReportPaginationParams

export type PaginatedReportByAsset = Record<string, AssetValues>

type AssetValues = {
  amount: number
  cost_basis: number
  value: number
}

export type FetchReportDownloadUrlParams = {
  start: string
  end: string
  source: string[]
  asset: string[]
  book_type: BookType
  include_address_source: boolean
  source_tags: string[]
  version?: string
}

export type FetchReportDownloadUrlResponse = {
  url: string
}
